import React, { useState, useEffect, useRef } from "react";
import { useToaster } from "@hellocontento/maillard";
import { format } from "date-fns";
import { callApi } from "utils/ContentoApi";
import ReactMarkdown from "react-markdown"

import * as styles from "../styles"

const EMPTY_STATE_COPY = `There are no custom instructions. Add your own to steer how this profile's posts are written, for example, "Posts must never exceed 250 characters." Instructions apply to the post text, not the visuals.`;

interface CustomInstructionsRowProps {
  accountId: string;
  profileId: string;
  customInstructions: string | null;
  onUpdatedAtChange: (updatedAt: string) => void; // keeps the card's optimistic lock current after this save
  onSaveStatusChange?: (status: { text: string; isError: boolean }) => void;
}

export const CustomInstructionsRow: React.FC<CustomInstructionsRowProps> = ({
  accountId,
  profileId,
  customInstructions,
  onUpdatedAtChange,
  onSaveStatusChange,
}) => {
  const addToast = useToaster();
  const [activeEditorTab, setActiveEditorTab] = useState<"edit" | "view">("view");
  // Seed from props once on mount; after that this row owns the value for its
  // entire lifetime, same as TextRow owns the profile content.
  const [editedInstructions, setEditedInstructions] = useState(customInstructions ?? "");
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState(false);
  const shouldSkipNextAutoSave = useRef(true);

  useEffect(() => {
    if (shouldSkipNextAutoSave.current) {
      shouldSkipNextAutoSave.current = false;
      return;
    }

    const timer = setTimeout(async () => {
      try {
        const response = await callApi({
          method: "put",
          url: `content-factory/accounts/${accountId}/profiles/${profileId}/custom-instructions`,
          data: { customInstructions: editedInstructions }
        }, true, true);
        onUpdatedAtChange(response.updatedAt);
        setLastSaved(new Date());
        setSaveError(false);
      } catch (error) {
        console.error("Failed to save custom instructions", error);
        setSaveError(true);
        addToast("Failed to save custom instructions", "error");
      }
    }, 1000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editedInstructions]);

  useEffect(() => {
    const text = saveError ? "Failed to save" : lastSaved ? `Saved at ${format(lastSaved, "HH:mm")}` : "Saved";
    if (lastSaved || saveError) onSaveStatusChange?.({ text, isError: saveError });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastSaved, saveError, onSaveStatusChange]);

  const hasInstructions = editedInstructions.trim().length > 0;

  return (
    <styles.ProfileSection>
      <styles.SegMini>
        <styles.SegMiniButton isActive={activeEditorTab === "view"} onClick={() => setActiveEditorTab("view")}>
          View
        </styles.SegMiniButton>
        <styles.SegMiniButton data-testid="instructions-edit-tab" isActive={activeEditorTab === "edit"} onClick={() => setActiveEditorTab("edit")}>
          Edit
        </styles.SegMiniButton>
      </styles.SegMini>

      <styles.Field $wide>
        {activeEditorTab === "edit" ? (
          <styles.ProfileTextArea
            value={editedInstructions}
            onChange={(event) => setEditedInstructions(event.target.value)}
          />
        ) : (
          <styles.FieldValue>
            {hasInstructions ? (
              <ReactMarkdown>{editedInstructions}</ReactMarkdown>
            ) : (
              <p>{EMPTY_STATE_COPY}</p>
            )}
          </styles.FieldValue>
        )}
      </styles.Field>
    </styles.ProfileSection>
  );
};

export default CustomInstructionsRow;